import React, { useEffect, useMemo, useState } from 'react';
import { Upload, Button, Alert, Tooltip } from 'antd';
import {
  InboxOutlined, EyeOutlined, DeleteOutlined, FilePdfOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CBModal from './CBModal';

const { Dragger } = Upload;

const PDF_MIME = 'application/pdf';

/**
 * GedFileField - champ d'upload mono-fichier PDF avec preview inline.
 *
 * Conçu pour les modales métier (PV, courrier, facture, expertise…).
 * Le parent contrôle le fichier sélectionné via `value`/`onChange`,
 * et décide lui-même à quel endpoint l'envoyer (ex: /v1/pv/{id}/document).
 *
 * Props :
 *   value        : File | null
 *   onChange     : (file: File|null) => void
 *   maxSizeMo    : nombre (défaut 20)
 *   hint         : texte affiché sous la dropzone (ex: "sera archivé avec le tag PV")
 *   disabled     : boolean
 *   required     : boolean (affiche un astérisque rouge si vide)
 */
export default function GedFileField({
  value,
  onChange,
  maxSizeMo = 20,
  hint,
  disabled = false,
  required = false,
}) {
  const { t } = useTranslation('commun');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  // Génère un URL blob éphémère pour la preview PDF
  useEffect(() => {
    if (!value) { setBlobUrl(null); return; }
    const url = URL.createObjectURL(value);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const handleBeforeUpload = (file) => {
    const isPdf = file.type === PDF_MIME || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      toast.error(t('gedFileField.formatNonAccepte'));
      return Upload.LIST_IGNORE;
    }
    const okSize = file.size / 1024 / 1024 < maxSizeMo;
    if (!okSize) {
      toast.error(t('gedFileField.tailleMax', { taille: maxSizeMo }));
      return Upload.LIST_IGNORE;
    }
    onChange?.(file);
    return false; // upload différé : le parent pilote
  };

  const sizeLabel = useMemo(() => {
    if (!value) return null;
    const kb = value.size / 1024;
    if (kb < 1024) return t('gedFileField.tailleKo', { taille: kb.toFixed(0) });
    return t('gedFileField.tailleMo', { taille: (kb / 1024).toFixed(1) });
  }, [value, t]);

  if (value) {
    return (
      <div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
          border: '1px solid #d5c93b40', background: '#fdfce8',
          borderRadius: 10,
        }}>
          <FilePdfOutlined style={{ fontSize: 26, color: '#dc2626' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {value.name}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{sizeLabel} · PDF</div>
          </div>
          <Tooltip title={t('gedFileField.previsualiser')}>
            <Button size="small" icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)} disabled={disabled} />
          </Tooltip>
          <Tooltip title={t('gedFileField.retirer')}>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onChange?.(null)} disabled={disabled} />
          </Tooltip>
        </div>

        <CBModal
          open={previewOpen}
          onCancel={() => setPreviewOpen(false)}
          footer={null}
          width={900}
          title={value.name}
          destroyOnClose
        >
          {blobUrl && (
            <iframe
              src={blobUrl}
              title={value.name}
              style={{ width: '100%', height: '70vh', border: '1px solid #e5e7eb', borderRadius: 6 }}
            />
          )}
        </CBModal>
      </div>
    );
  }

  return (
    <div>
      <Dragger
        name="file"
        multiple={false}
        maxCount={1}
        accept="application/pdf,.pdf"
        beforeUpload={handleBeforeUpload}
        showUploadList={false}
        disabled={disabled}
        style={{ background: '#fafafa', borderStyle: 'dashed', borderColor: '#1a6b3c' }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ color: '#1a6b3c' }} />
        </p>
        <p className="ant-upload-text" style={{ fontSize: 13 }}>
          {t('gedFileField.glisserIci')}
          {required && <span style={{ color: '#ef4444' }}> *</span>}
        </p>
        <p className="ant-upload-hint" style={{ fontSize: 11 }}>
          {t('gedFileField.pdfUniquement', { taille: maxSizeMo })}
          {hint && <> · {hint}</>}
        </p>
      </Dragger>

      {required && (
        <Alert
          type="info"
          showIcon
          message={t('gedFileField.pdfAttendu')}
          style={{ marginTop: 8, borderRadius: 8, fontSize: 12 }}
        />
      )}
    </div>
  );
}
